import { Button } from '@components/Button'
import { StatusBadge } from '@components/order/StatusBadge'
import { badgeStatusFor } from '@components/orders/badgeStatus'
import { IUserOrder, IUserOrderItem } from '@hooks/useUserOrders'
import { formatDateShort } from '@utils/dateFormatter'
import { formatMoney } from '@utils/moneyFormatter'
import { router } from 'expo-router'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { isWeb, Text, XStack, YStack } from 'tamagui'

interface IOrderRowProps {
  order: IUserOrder
  // HET-3540: pedido decomposto (order bump) vira UMA linha POR ITEM — cada uma com valor,
  // status e "Detalhes" próprios (o detalhe abre focado no item via ?item=<cartItemId>).
  item?: IUserOrderItem
}

export function OrderRow({ order, item }: IOrderRowProps) {
  const { t } = useTranslation('Orders')

  const goToDetails = useCallback(() => {
    const itemQuery = item?.cartItemId ? `?item=${item.cartItemId}` : ''
    // Full navigation so nginx decides whether to render members or hero_theme.
    if (isWeb) {
      window.location.href = `/orders/${order.id}/payments${itemQuery}`
    } else {
      router.push({
        pathname: '/orders/[orderId]/payments',
        params: item?.cartItemId
          ? { orderId: order.id, item: item.cartItemId }
          : { orderId: order.id },
      })
    }
  }, [order.id, item])

  const productTitle = item
    ? (item.name ?? '-')
    : (order.productTitle?.replace(/<br\s*\/?>/gi, ', ') ?? '-')

  const isSubscription = item
    ? !item.orderBump && order.isSubscription
    : order.isSubscription
  const status = badgeStatusFor(order, item)

  const totalCents = item ? (item.amountCents ?? 0) : (order.amountToPay ?? 0)

  return (
    <XStack
      p="$size.spacing-xs"
      br="$border-radius-sm"
      borderWidth="$borderWidth.border-width-hairline"
      borderColor="$border-strong"
      gap="$size.spacing-md"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
    >
      <XStack
        flex={1}
        minWidth={0}
        flexShrink={1}
        alignItems="center"
        gap="$size.spacing-xs"
        flexWrap="wrap"
      >
        <Text
          color="$text-primary"
          ff="$mulishSemiBold"
          fontSize="$font-size-sm"
          numberOfLines={2}
        >
          {productTitle}
          {item?.orderBump && (
            <Text
              color="$text-secondary"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
            >
              {` (${t('orderBump')})`}
            </Text>
          )}
        </Text>
        <StatusBadge status={status} isSubscription={isSubscription} />
      </XStack>

      <XStack gap="$size.spacing-xs" alignItems="center" flexShrink={0}>
        {/* minWidth em vez de width fixa: 80px cortava "25/08/2026" no meio
            ("25/08/20…") e a linha tem folga de sobra à esquerda. */}
        <YStack minWidth={96} flexShrink={0}>
          <Text
            color="$text-secondary"
            fontSize="$font-size-xxs"
            ff="$mulishRegular"
          >
            {t('columns.date')}
          </Text>
          <Text
            color="$text-primary"
            fontSize="$font-size-xs"
            ff="$mulishRegular"
            numberOfLines={1}
          >
            {order.createdAt ? formatDateShort(order.createdAt) : '-'}
          </Text>
        </YStack>

        <YStack minWidth={88} flexShrink={0}>
          <Text
            color="$text-secondary"
            fontSize="$font-size-xxs"
            ff="$mulishRegular"
          >
            {t('columns.total')}
          </Text>
          <Text
            color="$text-primary"
            fontSize="$font-size-xs"
            ff="$mulishSemiBold"
            numberOfLines={1}
          >
            {formatMoney(totalCents)}
          </Text>
        </YStack>

        <XStack flexShrink={0} justifyContent="flex-end">
          <Button link onPress={goToDetails}>
            <Text
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
              color="$accent"
              textDecorationLine="underline"
              numberOfLines={1}
            >
              {t('details')}
            </Text>
          </Button>
        </XStack>
      </XStack>
    </XStack>
  )
}
